// ============================================================
// CLUBHUB – Nachrichten: die Ansicht der Listen in der Adresse
// ============================================================
// Wie bei allen Listen (Agent `briefe/ansicht.ts`): Suche, Filter
// und Sortierung stehen in den searchParams — eine gefilterte Liste
// laesst sich ablegen und weitergeben.
//
// OHNE ANGABE SORTIERT DIE LISTE NACH DATUM, das juengste oben.
// Geplante Nachrichten zaehlen mit ihrem Termin, Entwuerfe mit dem
// letzten Speichern — gesucht wird meist die von neulich.
// ============================================================
import type { MailingChannel, MailingStatus, TemplateKind } from "@/generated/prisma/enums";
import { readSort, type SortColumns, type SortState } from "@/lib/sorting";
import type { MailingView, Zweck } from "./views";

export const STATUS_FILTER: { schluessel: string; status: MailingStatus[]; label: string; hinweis: string }[] = [
  { schluessel: "entwurf", status: ["DRAFT"], label: "Entwürfe", hinweis: "Noch nicht versendet" },
  { schluessel: "geplant", status: ["SCHEDULED"], label: "Geplant", hinweis: "Geht zu einem festen Termin hinaus" },
  { schluessel: "laeuft", status: ["SENDING"], label: "Wird versendet", hinweis: "Versand läuft gerade" },
  { schluessel: "versendet", status: ["SENT"], label: "Versendet", hinweis: "Hinausgegangen" },
  { schluessel: "fehler", status: ["FAILED"], label: "Fehlgeschlagen", hinweis: "Versand abgebrochen" },
];

export const KANAL_FILTER: { schluessel: string; channel: MailingChannel; label: string }[] = [
  { schluessel: "mail", channel: "EMAIL", label: "E-Mail" },
  { schluessel: "sms", channel: "SMS", label: "SMS" },
  { schluessel: "brief", channel: "LETTER", label: "Brief" },
];

export const ZWECK_FILTER: { schluessel: string; zweck: Zweck; label: string; hinweis: string }[] = [
  { schluessel: "info", zweck: "INFO", label: "Vereinsinformation", hinweis: "An alle gewählten Mitglieder" },
  { schluessel: "newsletter", zweck: "NEWSLETTER", label: "Newsletter", hinweis: "Nur mit Einwilligung" },
];

export const SORT_COLUMNS = {
  betreff: { label: "Betreff", first: "auf" },
  empfaenger: { label: "Empfänger", first: "ab" },
  status: { label: "Status", first: "auf" },
  datum: { label: "Datum", first: "ab" },
  zustellung: { label: "Zustellung", first: "ab" },
  oeffnung: { label: "Öffnungen", first: "ab" },
} as const satisfies SortColumns<string>;

export type SortKey = keyof typeof SORT_COLUMNS;
export const DEFAULT_SORT: SortKey = "datum";

export type Ansicht = {
  q: string;
  status: string;
  kanal: string;
  zweck: string;
  sort: SortState<SortKey>;
};

export const ALLE_PARAMS = ["q", "status", "kanal", "zweck", "sortierung", "richtung"] as const;

function eins(p: Record<string, string | string[] | undefined>, name: string): string {
  const w = p[name];
  return (Array.isArray(w) ? w[0] : w) ?? "";
}

export function ansichtLesen(p: Record<string, string | string[] | undefined>): Ansicht {
  const status = eins(p, "status");
  const kanal = eins(p, "kanal");
  const zweck = eins(p, "zweck");
  return {
    q: eins(p, "q").trim(),
    status: STATUS_FILTER.some((s) => s.schluessel === status) ? status : "",
    kanal: KANAL_FILTER.some((k) => k.schluessel === kanal) ? kanal : "",
    zweck: ZWECK_FILTER.some((z) => z.schluessel === zweck) ? zweck : "",
    sort: readSort(p, SORT_COLUMNS, DEFAULT_SORT),
  };
}

export function aktiveFilter(a: Ansicht): number {
  return [a.q, a.status, a.kanal, a.zweck].filter(Boolean).length;
}

/** Das Datum, nach dem eine Nachricht in der Liste steht. */
export function listenDatum(m: MailingView): string {
  return m.sentAt ?? m.scheduledAt ?? m.updatedAt;
}

/** Zugestellt in Prozent der Angeschriebenen — `null`, solange nichts hinaus ist. */
export function zustellquote(m: MailingView): number | null {
  if (m.stats.total === 0 || (m.status !== "SENT" && m.status !== "SENDING" && m.status !== "FAILED")) return null;
  return (m.stats.delivered / m.stats.total) * 100;
}

/** Geoeffnet in Prozent der zugestellten Mails — nur E-Mail kennt Oeffnungen. */
export function oeffnungsquote(m: MailingView): number | null {
  if (m.channel !== "EMAIL") return null;
  if (m.stats.emailDelivered <= 0) return null;
  return (m.stats.opened / m.stats.emailDelivered) * 100;
}

// ── Vorlagen ─────────────────────────────────────────────────

export const VORLAGEN_SORT = {
  name: { label: "Name", first: "auf" },
  art: { label: "Art", first: "auf" },
  geaendert: { label: "Geändert", first: "ab" },
  verwendet: { label: "Verwendet", first: "ab" },
} as const satisfies SortColumns<string>;

export type VorlagenSortKey = keyof typeof VORLAGEN_SORT;

export type VorlagenAnsicht = { q: string; art: TemplateKind | ""; sort: SortState<VorlagenSortKey> };

export const VORLAGEN_PARAMS = ["q", "art", "sortierung", "richtung"] as const;

export const VORLAGEN_ARTEN: TemplateKind[] = ["EMAIL", "WELCOME", "LETTER", "SMS", "DUNNING", "DONATION_RECEIPT", "INVOICE_TEXT"];

export function vorlagenAnsichtLesen(p: Record<string, string | string[] | undefined>): VorlagenAnsicht {
  const art = eins(p, "art");
  return {
    q: eins(p, "q").trim(),
    art: (VORLAGEN_ARTEN as string[]).includes(art) ? (art as TemplateKind) : "",
    sort: readSort(p, VORLAGEN_SORT, "name"),
  };
}

/** Welche Vorlagenarten zu welchem Kanal passen. */
export const ARTEN_FUER_KANAL: Record<MailingChannel, TemplateKind[]> = {
  EMAIL: ["EMAIL", "WELCOME", "DUNNING", "DONATION_RECEIPT"],
  LETTER: ["LETTER", "WELCOME", "DUNNING", "DONATION_RECEIPT"],
  SMS: ["SMS"],
};

/** Der Kanal, auf dem eine Vorlage zuerst gedacht ist. */
export function kanalFuerArt(kind: TemplateKind): MailingChannel {
  if (kind === "SMS") return "SMS";
  if (kind === "LETTER") return "LETTER";
  return "EMAIL";
}
